// platform/frontend/src/hooks/useAuditedPlanOperations.js
import { useCallback } from "react";
import { auditApi } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

export const useAuditedPlanOperations = () => {
  const { user } = useAuth();

  const auditPlanView = useCallback(async (plan) => {
    try {
      await auditApi.logEntry({
        action: "VIEW_PLAN",
        entity_type: "PLAN",
        entity_id: plan.plan_id,
        entity_identifier: `Plan: ${plan.plan_name} for ${plan.consumer_name}`,
        action_status: "SUCCESS",
        contains_phi: true,
        data_sensitivity_level: "high",
        phi_types: ["treatment_plan"],
        fields_accessed: [
          "consumer_name",
          "plan_details",
          "goals_objectives",
          "services",
        ],
      });
    } catch (error) {
      console.warn("Failed to log plan view audit:", error);
    }
  }, []);

  const auditPlanCreate = useCallback(async (planData, planId) => {
    try {
      await auditApi.logEntry({
        action: "CREATE_PLAN",
        entity_type: "PLAN",
        entity_id: planId,
        entity_identifier: `Plan: ${planData.plan_name}`,
        action_status: "SUCCESS",
        contains_phi: true,
        data_sensitivity_level: "high",
        new_values: {
          plan_name: planData.plan_name,
          consumer_id: planData.consumer_id,
          services_count: planData.services?.length || 0,
        },
      });
    } catch (error) {
      console.warn("Failed to log plan create audit:", error);
    }
  }, []);

  const auditPlanUpdate = useCallback(async (planId, planName, changes) => {
    try {
      await auditApi.logEntry({
        action: "UPDATE_PLAN",
        entity_type: "PLAN",
        entity_id: planId,
        entity_identifier: `Plan: ${planName}`,
        action_status: "SUCCESS",
        contains_phi: true,
        data_sensitivity_level: "high",
        old_values: changes.old,
        new_values: changes.new,
      });
    } catch (error) {
      console.warn("Failed to log plan update audit:", error);
    }
  }, []);

  const auditPlanDelete = useCallback(async (plan) => {
    try {
      await auditApi.logEntry({
        action: "DELETE_PLAN",
        entity_type: "PLAN",
        entity_id: plan.plan_id,
        entity_identifier: `Plan: ${plan.plan_name} for ${plan.consumer_name}`,
        action_status: "SUCCESS",
        contains_phi: true,
        data_sensitivity_level: "high",
      });
    } catch (error) {
      console.warn("Failed to log plan delete audit:", error);
    }
  }, []);

  const auditPlanExport = useCallback(async (plan) => {
    try {
      await auditApi.logEntry({
        action: "EXPORT_PLAN_PDF",
        entity_type: "PLAN",
        entity_id: plan.plan_id,
        entity_identifier: `Plan PDF: ${plan.plan_name}`,
        action_status: "SUCCESS",
        contains_phi: true,
        data_sensitivity_level: "high",
        purpose_of_use: "TREATMENT",
      });
    } catch (error) {
      console.warn("Failed to log plan export audit:", error);
    }
  }, []);

  return {
    auditPlanView,
    auditPlanCreate,
    auditPlanUpdate,
    auditPlanDelete,
    auditPlanExport,
  };
};
